/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ModalState } from '../../../redux/modal/reducers';

import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import InfiniteCircleLoaderArc from '../../loader/infiniteCircleLoaderArc';
import Modal, { ModalView } from '../modal';
import './ExternalContentModal.css';

// After 10 seconds, we declare the external content failed to load (in ms)
const LOAD_TIMEOUT = 10_000;

const ExternalContentModal = ({ ariaLabel, externalContentUrl }: ModalState): React.Node => {
  const loadTimer = useRef<TimeoutID | null>(null);
  const modalRef = useRef<ModalView | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const setModalRef = useCallback((instance: ModalView | null) => {
    modalRef.current = instance;
  }, []);

  const resetLoadTimer = useCallback(() => {
    if (loadTimer.current) {
      clearTimeout(loadTimer.current);
      loadTimer.current = null;
    }
  }, []);

  const handleMessagePosted = useCallback(
    (e: SyntheticInputEvent<HTMLElement>) => {
      const { data } = e;

      if (typeof data !== 'string') {
        return;
      }

      /*
       * Temporarily commented because of too many different domains for myVF
       *
       * if (!isSameDomain(process.env.REACT_APP_IFRAME_DOMAIN_ORIGIN, origin) && !(/^https?:\/\/localhost:3000$/ui).test(origin)) {
       *   // Message does not come from a trusted source
       *   return;
       * }
       */

      if (data === 'loaded') {
        // External content successfully loaded
        resetLoadTimer();
        setIsLoading(false);
      }
    },
    [resetLoadTimer],
  );

  const loadErrorCallback = useCallback(() => {
    Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('modal.external_content.load_error'));
    modalRef.current?.close();
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessagePosted, {
      capture: true,
      passive: true,
    });
    loadTimer.current = setTimeout(loadErrorCallback, LOAD_TIMEOUT);

    return () => {
      window.removeEventListener('message', handleMessagePosted, {
        capture: true,
        passive: true,
      });
      resetLoadTimer();
    };
  }, [handleMessagePosted, loadErrorCallback, resetLoadTimer]);

  /*
   * Reason for eslint-disable: The third-party content requires allow-same-origin to pass CORS API checks
   * and allow-scripts to execute its UI. Safe because it loads an external domain.
   */
  const children = (
    <>
      {/* eslint-disable-next-line react/iframe-missing-sandbox */}
      <iframe sandbox='allow-same-origin allow-scripts allow-forms allow-storage-access-by-user-activation' src={externalContentUrl} />
      {isLoading ? (
        <div className='loader'>
          <InfiniteCircleLoaderArc />
        </div>
      ) : null}
    </>
  );

  return (
    <Modal ariaLabel={ariaLabel} className='externalContent' innerRef={setModalRef}>
      {children}
    </Modal>
  );
};

export default ExternalContentModal;
